/*
 * Copyright (c) Nexign, JSC, 2023.
 */
/**
 *
 * @author Evgeniy Naumov
 * @version 1.26.0
 * @since 1.25.1
 */

import { useRef } from 'react';

import { Button } from '@nexign/scc-ui-kit';
import { Formik, FormikProps, useFormikContext } from 'formik';
import { useSelector } from 'react-redux';

import AttachedItem from '@/main/components/common/AttachedItem';
import { makeSelectTranslationCommon } from '@/main/modules/internationalization/intSelectors';
import { FormSubmitHandler } from '@/main/types/globalTypes';
import { stringFormatParams } from '@/main/utils/stringFormat';

import FormField, { IFormFieldProps } from '../FormField';
import FormFieldInput from '../FormFieldInput';
import { EFormImageLinksFileds, defaultFormImageLinksValues } from './constants';
import { FormImageLinksAttachedList, FormImageLinksContainerStyled } from './FormImageLinksStyled';
import { IFormImageLinks } from './types';
import { getFormImageLinksValidationSchema } from './validation';

interface IFormImageLinksProps extends IFormFieldProps {
    buttonText: string;
    maxItems: number;
    inputPlaceholder?: string;
}

export const FormImageLinks = (props: IFormImageLinksProps) => {
    const { buttonText, maxItems, inputPlaceholder = '', ...fieldProps } = props;

    const tCorporate = useSelector(makeSelectTranslationCommon());

    const inputFormRef = useRef<FormikProps<IFormImageLinks>>(null);

    const { setFieldValue } = useFormikContext();

    const validationSchema = getFormImageLinksValidationSchema(tCorporate.form);

    return (
        <FormField {...fieldProps}>
            {({ field, label, labelHint, dataName }) => {
                const handleSubmit: FormSubmitHandler<IFormImageLinks> = (
                    data,
                    { setSubmitting, resetForm, setFieldError },
                ) => {
                    if (Array.isArray(field.value)) {
                        if (field.value.length >= maxItems) {
                            setFieldError(
                                EFormImageLinksFileds.INPUT,
                                stringFormatParams(tCorporate.form.validation['field.number.max'], {
                                    number: maxItems,
                                }),
                            );
                            return;
                        }
                        setFieldValue(field.name, [...field.value, data[EFormImageLinksFileds.INPUT]]);
                        resetForm();
                        setSubmitting(false);
                    }
                };

                const handleDelete = (id?: number) => {
                    if (Array.isArray(field.value) && !!field.value.length) {
                        if (inputFormRef.current && field.value.length === maxItems) {
                            inputFormRef.current.setFieldTouched(EFormImageLinksFileds.INPUT, false, true);
                        }
                        if (typeof id === 'number') {
                            setFieldValue(
                                field.name,
                                field.value.filter((_, index) => id !== index),
                            );
                        }
                    }
                };

                return (
                    <>
                        <Formik<IFormImageLinks>
                            enableReinitialize
                            initialValues={defaultFormImageLinksValues}
                            innerRef={inputFormRef}
                            validationSchema={validationSchema}
                            onSubmit={handleSubmit}
                        >
                            {(formData) => {
                                const { isSubmitting, isValidating, isValid, dirty } = formData;
                                const submitDisabled = isSubmitting || isValidating || !isValid || !dirty;

                                const handleBtnClick = () => {
                                    if (submitDisabled) return;
                                    handleSubmit(formData.values, formData);
                                };
                                return (
                                    <>
                                        <FormImageLinksContainerStyled>
                                            <FormFieldInput
                                                dataName={dataName}
                                                label={label}
                                                labelHint={labelHint}
                                                name={EFormImageLinksFileds.INPUT}
                                                placeholder={inputPlaceholder}
                                            />
                                            <Button
                                                disabled={submitDisabled}
                                                typographyProps={{ variant: 'h5' }}
                                                onClick={handleBtnClick}
                                            >
                                                {buttonText}
                                            </Button>
                                        </FormImageLinksContainerStyled>
                                    </>
                                );
                            }}
                        </Formik>
                        {!!field.value && Array.isArray(field.value) && !!field.value.length && (
                            <FormImageLinksAttachedList>
                                {field.value.map((el, index) => (
                                    <AttachedItem id={index} key={index} title={el} onDelete={handleDelete} />
                                ))}
                            </FormImageLinksAttachedList>
                        )}
                    </>
                );
            }}
        </FormField>
    );
};

export default FormImageLinks;
